import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { API_ROOT_URL } from "../../config/api";

interface Product {
    id: string;
    name?: string;
    title?: string;
    description?: string;
    launchDate?: string;
    start_date?: string;
    endDate?: string;
    end_date?: string;
    notes?: string;
    status?: string;
    image?: string;
    video?: string;
    tags?: string[];
    events?: Array<{ id?: string; name?: string; eventName?: string }>;
    assets?: Array<{ id?: string; name?: string; original_filename?: string }>;
    assetIds?: string[];
    [key: string]: any;
}

const authenticatedHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("accessToken") || ""}`,
});

const formatDate = (value?: string) => {
    if (!value) return "Not specified";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const displayValue = (value?: string) =>
    value?.trim() ? value : "Not specified";

// Linked events open in the Library Events page, so the header + breadcrumb
// are attached and pinned while scrolling (same as Library → Events).
const LIBRARY_EVENTS_PATH = "/customer-manager/library/events";

// ## 0. Add above the component
const THEME_PRIMARY = "var(--color-primary, #059669)";
const THEME_PRIMARY_SOFT = "color-mix(in srgb, var(--color-primary, #059669) 12%, white)";
const THEME_PRIMARY_RING = "color-mix(in srgb, var(--color-primary, #059669) 45%, transparent)";

const ICONS = {
    calendar: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
    details: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
    image: "M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z",
    video: "M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z",
    edit: "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z",
    chevronRight: "M9 5l7 7-7 7",
};

const icon = (d: string, className = "w-5 h-5") => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
    </svg>
);

const normalizeStatus = (s: unknown): string => {
    const raw = String(s ?? "").trim();
    const v = raw.toUpperCase();
    if (v === "ACTIVE") return "Active";
    if (v === "ARCHIVED") return "Archived";
    if (v === "SCHEDULED") return "Scheduled";
    return raw;
};

const getStatusColor = (s: string) => s === "Active" ? "bg-green-100 text-green-700" : s === "Scheduled" ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-700";
const getStatusIcon = (s: string) => (s === "Active" ? "✅" : "📦");
const isVideoAsset = (name = "", container?: string | null) => /\.(mp4|mov|webm|mkv|avi|m4v)$/i.test(name) || ["mp4", "mov", "webm", "mkv", "avi", "quicktime"].includes((container || "").toLowerCase());

// Same id that LibraryPage renders and CampaignsAndEvents.tsx portals into
const LIBRARY_BREADCRUMB_SLOT_ID = "library-breadcrumb-slot";

function useLibraryBreadcrumbSlot(): HTMLElement | null { const [slot, setSlot] = useState<HTMLElement | null>(null); useEffect(() => { setSlot(document.getElementById(LIBRARY_BREADCRUMB_SLOT_ID)); }, []); return slot; }

const ProductDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const breadcrumbSlot = useLibraryBreadcrumbSlot();

    // Open at the top so the header and breadcrumb are in view
    useEffect(() => { window.scrollTo({ top: 0, behavior: "auto" }); }, [id]);
    const [product, setProduct] = useState<Product | null>(location.state?.product ?? null,);
    const [loading, setLoading] = useState(!location.state?.product);
    const [error, setError] = useState("");
    const loggedInUser = JSON.parse(localStorage.getItem("user") || "{}");
    const tenantId = loggedInUser.tenantId;
    const returnPath = location.state?.returnPath || "/customer-manager/dashboard?tab=products-only";

    useEffect(() => {
        if (!id) { setError("Product ID is missing."); setLoading(false); return; }
        let cancelled = false;
        const loadProduct = async () => {
            try {
                setLoading(true);
                setError("");
                const response = await fetch(`${API_ROOT_URL}/products/${encodeURIComponent(id)}`, { method: "GET", headers: authenticatedHeaders(), });
                const result = await response.json();
                if (!response.ok || !result.success) { throw new Error(result.message || "Failed to load product details."); }

                const data = result.data ?? result.product ?? result;

                if (!data || Array.isArray(data) || !data.product_id) {
                    throw new Error("Product details were not found.");
                }

                // Fetch actual linked assets and events
                const [assetResponse, eventResponse] = await Promise.all([
                    fetch(
                        `${API_ROOT_URL}/product-assets/${encodeURIComponent(id)}`,
                        {
                            method: "GET",
                            headers: authenticatedHeaders(),
                        }
                    ),
                    fetch(
                        `${API_ROOT_URL.replace(/\/api\/?$/, "")}/events/products/${encodeURIComponent(id)}/events`,
                        {
                            method: "GET",
                            headers: authenticatedHeaders(),
                        }
                    ),
                ]);

                const assetResult = await assetResponse.json();
                const eventResult = await eventResponse.json();

                if (!assetResponse.ok || assetResult.success === false) {
                    throw new Error(assetResult.message || "Failed to load linked assets.");
                }

                if (!eventResponse.ok || eventResult.success === false) {
                    throw new Error(eventResult.message || "Failed to load linked events.");
                }

                const linkedAssetRecords = Array.isArray(assetResult.data)
                    ? assetResult.data
                    : [];

                const linkedEventRecords = Array.isArray(eventResult.data)
                    ? eventResult.data
                    : [];

                if (!cancelled) {
                    setProduct({
                        ...data,
                        id: String(data.product_id),
                        name: data.title || "",
                        description: data.description || "",
                        notes: data.notes || "",
                        launchDate: data.start_date || "",
                        endDate: data.end_date || "",
                        tags: Array.isArray(data.tags) ? data.tags : [],

                        events: linkedEventRecords.map((event: any) => ({
                            ...event,
                            id: String(event.event_id ?? event.id),
                            name: event.title || event.name || "Untitled Event",
                        })),

                        assets: linkedAssetRecords.map((asset: any) => ({
                            ...asset,
                            id: String(asset.asset_id ?? asset.id),
                            name:
                                asset.original_filename ||
                                asset.filename ||
                                asset.name ||
                                "Untitled Asset",
                        })),
                    });
                }

            } catch (err: any) { if (!cancelled) { setProduct(null); setError(err.message || "Unable to load product details."); } }
            finally { if (!cancelled) { setLoading(false); } }
        };
        void loadProduct();
        return () => { cancelled = true; };
    }, [id]);

    // Only show actual events and assets linked to this product
    const linkedEvents = product?.events || [];
    const linkedAssets = product?.assets || [];

    if (loading && !product) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center px-4">
                <p className="text-sm text-gray-500">Loading product details...</p>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="max-w-4xl mx-auto p-6 sm:p-8">
                <button
                    type="button"
                    onClick={() => navigate(returnPath)}
                    className="mb-6 text-sm font-medium text-purple-600 hover:text-purple-800"
                >
                    ← Back to Products
                </button>

                <div className="rounded-xl border border-red-200 bg-red-50 p-6">
                    <h1 className="text-lg font-semibold text-red-800">
                        Unable to load product
                    </h1>
                    <p className="mt-2 text-sm text-red-700">
                        {error || "The requested product could not be found."}
                    </p>
                </div>
            </div>
        );
    }

    // ## 1. Replace the variables before `return`
    const productName = product.name || product.title || "Untitled Product";
    const status = normalizeStatus(product.status || "");
    const cardClass = "bg-white p-5 rounded-xl border border-gray-200 shadow-sm";
    const emptyDash = <span className="text-gray-400">—</span>;
    const goEdit = () => navigate(`/customer-manager/products/edit/${product.id}`, { state: { product, returnPath } });

    // ## 2. Replace the outer wrappers
    return (
        <div className="space-y-5 mx-auto max-w-7xl px-4 pb-5 pt-4 sm:px-6 lg:px-8">
            {(() => {
                const nav = (
                    <nav aria-label="Breadcrumb" className="flex items-center flex-wrap gap-1 text-sm">
                        <button
                            type="button"
                            onClick={() => navigate(returnPath)}
                            className="text-gray-500 hover:underline"
                            onMouseEnter={(e) => (e.currentTarget.style.color = THEME_PRIMARY)}
                            onMouseLeave={(e) => (e.currentTarget.style.color = "")}
                        >
                            Products
                        </button>
                        {icon(ICONS.chevronRight, "w-4 h-4 text-gray-300 shrink-0")}
                        <span className="font-semibold text-gray-900 truncate max-w-[240px]" aria-current="page" title={productName}>
                            {productName}
                        </span>
                    </nav>
                );

                // Inside LibraryPage: render into the pinned bar under the app header
                if (breadcrumbSlot) return createPortal(nav, breadcrumbSlot);

                // Fallback if the page is opened outside LibraryPage:
                // full-width attached bar, no card, no rounding
                return (
                    <div className="sticky top-0 z-30 -mx-4 -mt-5 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 bg-white border-b border-gray-200">
                        {nav}
                    </div>
                );
            })()}

            {/* ## 4. Header removed — Edit button moved into hero */}

            {/* ## 5. Product overview → hero card */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="relative h-56 sm:h-64 bg-gray-100">
                    {product.video ? (
                        <video src={product.video} controls className="w-full h-full object-contain bg-black" />
                    ) : product.image ? (
                        <img src={product.image} alt={productName} className="w-full h-full object-cover" />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                            <span className="text-6xl">📦</span>
                        </div>
                    )}
                </div>
                <div className="p-5 flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div className="min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                            <h2 className="text-2xl font-bold text-gray-900 break-words">{productName}</h2>
                            {status && (
                                <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${getStatusColor(status)}`}>
                                    {getStatusIcon(status)} {status}
                                </span>
                            )}
                        </div>
                        {product.launchDate && (
                            <p className="mt-2 flex items-center gap-1.5 text-sm text-gray-500">
                                {icon(ICONS.calendar, "w-4 h-4")}
                                {formatDate(product.launchDate)}
                                {product.endDate && ` – ${formatDate(product.endDate)}`}
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={goEdit}
                        className="shrink-0 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-xl shadow-sm hover:opacity-90 transition-opacity"
                        style={{ backgroundColor: THEME_PRIMARY }}
                    >
                        {icon(ICONS.edit, "w-4 h-4")}
                        Edit product
                    </button>
                </div>
            </div>

            {/* ## 6. Additional information + Tags → About card + right column */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
                <div className={cardClass}>
                    <div className="flex items-center gap-2 mb-4">
                        <span style={{ color: THEME_PRIMARY }}>{icon(ICONS.details)}</span>
                        <h4 className="text-base font-semibold text-gray-900">About</h4>
                    </div>
                    <dl className="space-y-4 text-sm">
                        {([["Description", product.description], ["Notes", product.notes]] as const).map(([label, value]) => (
                            <div key={label}>
                                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</dt>
                                <dd className="mt-1 text-gray-800 whitespace-pre-line">{value || emptyDash}</dd>
                            </div>
                        ))}
                        <div>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Tags</dt>
                            <dd className="mt-1 flex flex-wrap gap-1.5">
                                {product.tags?.length
                                    ? product.tags.map((tag, i) => (
                                        <span
                                            key={`${tag}-${i}`}
                                            className="px-2 py-0.5 text-xs font-medium rounded-full border"
                                            style={{ backgroundColor: THEME_PRIMARY_SOFT, borderColor: THEME_PRIMARY_RING, color: THEME_PRIMARY }}
                                        >
                                            #{tag}
                                        </span>
                                    ))
                                    : emptyDash}
                            </dd>
                        </div>
                    </dl>
                </div>

                <div className="lg:col-span-2 space-y-5">
                    {/* ## 7. Linked events */}
                    <div className={cardClass}>
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <span style={{ color: THEME_PRIMARY }}>{icon(ICONS.calendar)}</span>
                                <h4 className="text-base font-semibold text-gray-900">Linked events</h4>
                                <span className="min-w-[22px] text-center rounded-full px-1.5 text-xs leading-5"
                                    style={{ backgroundColor: THEME_PRIMARY_SOFT, color: THEME_PRIMARY }}>
                                    {linkedEvents.length}
                                </span>
                            </div>
                        </div>
                        {linkedEvents.length ? (
                            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {linkedEvents.map((event: any, index: number) => (
                                    <li key={event.id || index}>
                                        <button
                                            type="button"
                                            title={`Open ${event.name}`}
                                            onClick={() =>
                                                navigate(LIBRARY_EVENTS_PATH, {
                                                    state: {
                                                        openEventId: event.id,
                                                        openEvent: event,
                                                        returnPath: location.pathname + location.search,
                                                        fromProduct: {
                                                            id: product.id,
                                                            name: productName,
                                                            path: location.pathname + location.search,
                                                            state: { product, returnPath },
                                                        },
                                                    },
                                                })
                                            }
                                            className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50/50 text-left hover:bg-white hover:border-gray-300 hover:shadow-sm transition"
                                        >
                                            <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 text-lg bg-white border border-gray-200">🎪</span>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-medium text-gray-800 truncate">{event.name}</p>
                                                <p className="text-xs text-gray-400">Open event details</p>
                                            </div>
                                            {icon(ICONS.chevronRight, "w-4 h-4 text-gray-400 shrink-0")}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                                <p className="text-sm text-gray-500">No events linked yet.</p>
                            </div>
                        )}
                    </div>

                    {/* ## 8. Linked assets */}
                    <div className={cardClass}>
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <span style={{ color: THEME_PRIMARY }}>{icon(ICONS.image)}</span>
                                <h4 className="text-base font-semibold text-gray-900">Linked assets</h4>
                                <span className="min-w-[22px] text-center rounded-full px-1.5 text-xs leading-5"
                                    style={{ backgroundColor: THEME_PRIMARY_SOFT, color: THEME_PRIMARY }}>
                                    {linkedAssets.length}
                                </span>
                            </div>
                        </div>
                        {linkedAssets.length ? (
                            <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
                                {linkedAssets.map((asset: any, index: number) => {
                                    const video = isVideoAsset(asset.name, asset.container);
                                    const meta = [asset.status, asset.container].filter(Boolean).join(" • ");
                                    return (
                                        <li key={asset.id || index}>
                                            <div className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50/50">
                                                <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-white border border-gray-200"
                                                    style={{ color: THEME_PRIMARY }}>
                                                    {icon(video ? ICONS.video : ICONS.image, "w-5 h-5")}
                                                </span>
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm font-medium text-gray-800 truncate">{asset.name}</p>
                                                    {meta && <p className="text-xs text-gray-400 truncate">{meta}</p>}
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        ) : (
                            <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                                <p className="text-sm text-gray-500">No assets are linked to this product yet.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductDetailsPage;